import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { Pagination } from '@/components/shared/pagination';
import { StatCard } from '@/components/shared/stat-card';
import {
  PlatformSmsCheckoutDetailDialog,
  type PlatformSmsCheckoutRow,
} from '@/components/platform-sms-checkout-detail-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatDate, formatDateTime, formatMoney } from '@/utils/format';

interface CreditsSummary {
  currency: string;
  churchesWithBalance: number;
  totalBalanceMinor: number;
  totalPurchasedMinor: number;
  totalConsumedMinor: number;
  checkoutTotals: Array<{ status: string; count: number; amountMinor: number }>;
}

interface ChurchBalanceRow {
  churchId: string;
  churchName: string;
  balanceMinor: number;
  currency: string;
  purchasedMinor: number;
  consumedMinor: number;
  lastPurchaseAt: string | null;
}

interface CheckoutRow extends PlatformSmsCheckoutRow {}

type SmsCreditsTab = 'balances' | 'checkouts';

type CheckoutFilter = 'all' | 'pending' | 'processing' | 'paid' | 'failed' | 'cancelled' | 'expired';

const CHECKOUT_FILTERS: CheckoutFilter[] = [
  'all',
  'pending',
  'processing',
  'paid',
  'failed',
  'cancelled',
  'expired',
];

const checkoutBadgeVariant = (status: string): BadgeVariant => {
  if (status === 'paid') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'cancelled' || status === 'expired') return 'neutral';
  return 'info';
};

export const SmsCreditsSection = () => {
  const { t } = useTranslation('admin');
  const isAdmin = useIsPlatformAdmin();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [balancePage, setBalancePage] = useState(1);
  const [balancePageSize, setBalancePageSize] = useState(25);
  const [balanceSearch, setBalanceSearch] = useState('');
  const [balanceSearchInput, setBalanceSearchInput] = useState('');

  const [activeTab, setActiveTab] = useState<SmsCreditsTab>('balances');
  const [checkoutPage, setCheckoutPage] = useState(1);
  const [checkoutPageSize, setCheckoutPageSize] = useState(25);
  const [checkoutFilter, setCheckoutFilter] = useState<CheckoutFilter>('all');
  const [selectedCheckout, setSelectedCheckout] = useState<CheckoutRow | null>(null);
  const [checkoutDetailOpen, setCheckoutDetailOpen] = useState(false);

  const summary = useQuery({
    queryKey: ['platform-sms-credits-summary'],
    queryFn: () => api.get<CreditsSummary>('/platform/sms/credits/summary'),
  });

  const balances = useQuery({
    queryKey: ['platform-sms-balances', balancePage, balancePageSize, balanceSearch],
    queryFn: () =>
      api.get<{ data: ChurchBalanceRow[]; page: { page: number; pageSize: number; totalItems: number; totalPages: number } }>(
        '/platform/sms/credits/balances',
        {
          query: {
            page: balancePage,
            pageSize: balancePageSize,
            ...(balanceSearch ? { search: balanceSearch } : {}),
          },
        },
      ),
  });

  const checkouts = useQuery({
    queryKey: ['platform-sms-checkouts', checkoutPage, checkoutPageSize, checkoutFilter],
    queryFn: () =>
      api.get<{ data: CheckoutRow[]; page: { page: number; pageSize: number; totalItems: number; totalPages: number } }>(
        '/platform/sms/credits/checkout-sessions',
        {
          query: {
            page: checkoutPage,
            pageSize: checkoutPageSize,
            ...(checkoutFilter === 'all' ? {} : { status: checkoutFilter }),
          },
        },
      ),
  });

  const reconcile = useMutation({
    mutationFn: () => api.post<{ processed: number }>('/platform/sms/credits/reconcile', {}),
    onSuccess: async (result) => {
      toast.success(t('smsCredits.reconcileDone', { count: result.processed }));
      await queryClient.invalidateQueries({ queryKey: ['platform-sms-credits-summary'] });
      await queryClient.invalidateQueries({ queryKey: ['platform-sms-balances'] });
      await queryClient.invalidateQueries({ queryKey: ['platform-sms-checkouts'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const currency = summary.data?.currency ?? 'GHS';
  const stuckCount =
    summary.data?.checkoutTotals
      .filter((row) => row.status === 'processing' || row.status === 'pending' || row.status === 'failed')
      .reduce((sum, row) => sum + row.count, 0) ?? 0;

  const balanceColumns: DataTableColumn<ChurchBalanceRow>[] = [
    {
      id: 'church',
      header: t('smsCredits.columns.church'),
      cell: (row) => row.churchName,
    },
    {
      id: 'balance',
      header: t('smsCredits.columns.balance'),
      align: 'right',
      cell: (row) => formatMoney(row.balanceMinor, row.currency),
    },
    {
      id: 'purchased',
      header: t('smsCredits.columns.purchased'),
      align: 'right',
      cell: (row) => formatMoney(row.purchasedMinor, row.currency),
    },
    {
      id: 'consumed',
      header: t('smsCredits.columns.consumed'),
      align: 'right',
      cell: (row) => formatMoney(row.consumedMinor, row.currency),
    },
    {
      id: 'lastPurchase',
      header: t('smsCredits.columns.lastPurchase'),
      cell: (row) => (row.lastPurchaseAt ? formatDate(row.lastPurchaseAt) : '—'),
      hideBelowMd: true,
    },
  ];

  const checkoutColumns: DataTableColumn<CheckoutRow>[] = [
    {
      id: 'date',
      header: t('smsCredits.columns.date'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'church',
      header: t('smsCredits.columns.church'),
      cell: (row) => row.churchName,
    },
    {
      id: 'amount',
      header: t('smsCredits.columns.amount'),
      align: 'right',
      cell: (row) => formatMoney(row.amountMinor, row.currency),
    },
    {
      id: 'status',
      header: t('smsCredits.columns.status'),
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={checkoutBadgeVariant(row.status)}>
            {t(`smsCredits.checkoutStatus.${row.status as 'paid'}`)}
          </Badge>
          {isAdmin &&
          row.status !== 'paid' &&
          row.status !== 'cancelled' &&
          row.status !== 'expired' ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                openCheckoutDetail(row);
              }}
            >
              {t('smsCredits.recheck')}
            </Button>
          ) : null}
        </div>
      ),
    },
    {
      id: 'reference',
      header: t('smsCredits.columns.paymentReference'),
      cell: (row) => (
        <span className="font-mono text-body-sm">{row.paymentReference}</span>
      ),
      hideBelowMd: true,
    },
  ];

  const openCheckoutDetail = (row: CheckoutRow) => {
    setActiveTab('checkouts');
    setSelectedCheckout(row);
    setCheckoutDetailOpen(true);
  };

  const balancePageMeta = balances.data?.page;
  const checkoutPageMeta = checkouts.data?.page;

  return (
    <div className="space-y-8">
      {isAdmin ? (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            loading={reconcile.isPending}
            onClick={() => reconcile.mutate()}
          >
            {t('smsCredits.reconcile')}
          </Button>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('smsCredits.stats.totalBalance')}
          value={formatMoney(summary.data?.totalBalanceMinor ?? 0, currency)}
          loading={summary.isLoading}
        />
        <StatCard
          label={t('smsCredits.stats.totalPurchased')}
          value={formatMoney(summary.data?.totalPurchasedMinor ?? 0, currency)}
          loading={summary.isLoading}
        />
        <StatCard
          label={t('smsCredits.stats.totalConsumed')}
          value={formatMoney(summary.data?.totalConsumedMinor ?? 0, currency)}
          loading={summary.isLoading}
        />
        <StatCard
          label={t('smsCredits.stats.churchesWithBalance')}
          value={String(summary.data?.churchesWithBalance ?? 0)}
          hint={
            stuckCount > 0
              ? t('smsCredits.stats.stuckCheckouts', { count: stuckCount })
              : undefined
          }
          loading={summary.isLoading}
        />
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as SmsCreditsTab)}
      >
        <TabsList>
          <TabsTrigger value="balances">{t('smsCredits.tabs.balances')}</TabsTrigger>
          <TabsTrigger value="checkouts">{t('smsCredits.tabs.checkouts')}</TabsTrigger>
        </TabsList>

        <TabsContent value="balances" className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-body font-semibold text-fg">{t('smsCredits.balancesTitle')}</h2>
              <p className="text-body-sm text-fg-secondary">{t('smsCredits.balancesDescription')}</p>
            </div>
            <form
              className="flex w-full max-w-sm gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                setBalanceSearch(balanceSearchInput.trim());
                setBalancePage(1);
              }}
            >
              <Input
                value={balanceSearchInput}
                onChange={(event) => setBalanceSearchInput(event.target.value)}
                placeholder={t('smsCredits.searchChurches')}
                aria-label={t('smsCredits.searchChurches')}
              />
              <Button type="submit" variant="secondary">
                {t('smsCredits.search')}
              </Button>
            </form>
          </div>
          <DataTable
            rows={balances.data?.data ?? []}
            columns={balanceColumns}
            rowKey={(row) => row.churchId}
            loading={balances.isLoading}
            empty={
              balances.isError ? (
                <p className="text-body-sm text-danger">{tErrors('UNEXPECTED')}</p>
              ) : (
                <p className="text-body-sm text-fg-secondary">{t('smsCredits.balancesEmpty')}</p>
              )
            }
          />
          {balancePageMeta && balancePageMeta.totalPages > 1 ? (
            <Pagination
              page={balancePageMeta.page}
              pageSize={balancePageMeta.pageSize}
              totalItems={balancePageMeta.totalItems}
              totalPages={balancePageMeta.totalPages}
              onPageChange={setBalancePage}
              onPageSizeChange={(next) => {
                setBalancePageSize(next);
                setBalancePage(1);
              }}
              labels={{
                showing: ({ from, to, total }) => t('pagination.showing', { from, to, total }),
                perPage: t('pagination.perPage', { defaultValue: 'Per page' }),
                first: t('pagination.first', { defaultValue: 'First' }),
                previous: t('pagination.previous'),
                next: t('pagination.next'),
                last: t('pagination.last', { defaultValue: 'Last' }),
              }}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="checkouts" className="space-y-3">
          <div>
            <h2 className="text-body font-semibold text-fg">{t('smsCredits.checkoutsTitle')}</h2>
            <p className="text-body-sm text-fg-secondary">{t('smsCredits.checkoutsDescription')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {CHECKOUT_FILTERS.map((value) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={checkoutFilter === value ? 'default' : 'secondary'}
                onClick={() => {
                  setCheckoutFilter(value);
                  setCheckoutPage(1);
                }}
              >
                {t(`smsCredits.filters.${value}`)}
              </Button>
            ))}
          </div>
          <DataTable
            rows={checkouts.data?.data ?? []}
            columns={checkoutColumns}
            rowKey={(row) => row.id}
            loading={checkouts.isLoading}
            empty={<p className="text-body-sm text-fg-secondary">{t('smsCredits.checkoutsEmpty')}</p>}
            onRowClick={openCheckoutDetail}
          />
          {checkoutPageMeta && checkoutPageMeta.totalPages > 1 ? (
            <Pagination
              page={checkoutPageMeta.page}
              pageSize={checkoutPageMeta.pageSize}
              totalItems={checkoutPageMeta.totalItems}
              totalPages={checkoutPageMeta.totalPages}
              onPageChange={setCheckoutPage}
              onPageSizeChange={(next) => {
                setCheckoutPageSize(next);
                setCheckoutPage(1);
              }}
              labels={{
                showing: ({ from, to, total }) => t('pagination.showing', { from, to, total }),
                perPage: t('pagination.perPage', { defaultValue: 'Per page' }),
                first: t('pagination.first', { defaultValue: 'First' }),
                previous: t('pagination.previous'),
                next: t('pagination.next'),
                last: t('pagination.last', { defaultValue: 'Last' }),
              }}
            />
          ) : null}
        </TabsContent>
      </Tabs>

      <PlatformSmsCheckoutDetailDialog
        key={selectedCheckout?.id ?? 'closed'}
        checkout={selectedCheckout}
        open={checkoutDetailOpen}
        onOpenChange={(next) => {
          setCheckoutDetailOpen(next);
          if (!next) setSelectedCheckout(null);
        }}
        onRecheckSuccess={async () => {
          await queryClient.invalidateQueries({ queryKey: ['platform-sms-credits-summary'] });
          await queryClient.invalidateQueries({ queryKey: ['platform-sms-balances'] });
          await queryClient.invalidateQueries({ queryKey: ['platform-sms-checkouts'] });
        }}
      />
    </div>
  );
};
